<template>
    <div
        class="flex items-center gap-4 py-4"
        :class="{ 'cursor-pointer': !current && !joining }"
        @click="handleClick"
    >
        <img
            v-if="tenant.logo"
            :src="tenant.logo"
            :alt="tenant.name"
            class="border-muval-gray-4 size-12 shrink-0 rounded-lg border object-contain"
        />
        <div
            v-else
            class="flex size-12 shrink-0 items-center justify-center rounded-lg text-[15px] font-medium"
            :style="avatarStyle"
        >
            {{ initials }}
        </div>
        <div class="min-w-0 flex-1">
            <div class="text-muval-gray-1 text-[13.6px] font-semibold">{{ tenant.name }}</div>
            <div class="text-muval-gray-3 text-[11.9px]">{{ subtitle }}</div>
        </div>
        <div class="flex shrink-0 items-center gap-4">
            <button
                v-if="showLeave && !joining && !isSuperUser"
                class="text-xs text-[#e5162a] hover:underline"
                :disabled="leavingTenantId === tenant.id"
                @click.stop="emit('leave', tenant)"
            >
                Leave
            </button>
            <span
                v-if="joining"
                class="text-muval-gray-2 text-xs"
                >Joining...</span
            >
            <SvgIcon
                v-if="!current && !joining"
                name="system-forward"
                class="text-muval-gray-3 size-5"
            />
        </div>
    </div>
</template>

<script setup>
const userStore = useUserStore();
const isSuperUser = computed(() => userStore.isMoveproSuperAdminUser);

const props = defineProps({
    tenant: { type: Object, required: true },
    loading: { type: Boolean, default: false },
    current: { type: Boolean, default: false },
    joining: { type: Boolean, default: false },
    leavingTenantId: { type: Number, default: null },
    showLeave: { type: Boolean, default: true },
});

const emit = defineEmits(['select', 'leave']);

const handleClick = () => {
    if (!props.current && !props.joining) {
        emit('select', props.tenant);
    }
};

const initials = computed(() => {
    if (!props.tenant.name) return '';
    const words = props.tenant.name.trim().split(/\s+/);
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
});

function hexToRgba(hex, alpha) {
    if (!hex) return `rgba(31, 61, 240, ${alpha})`;
    const cleaned = hex.replace('#', '');
    const r = parseInt(cleaned.substring(0, 2), 16);
    const g = parseInt(cleaned.substring(2, 4), 16);
    const b = parseInt(cleaned.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const accentColor = computed(() => props.tenant.accent || '#1F3DF0');

const avatarStyle = computed(() => ({
    backgroundColor: hexToRgba(accentColor.value, 0.1),
    color: accentColor.value,
}));

const roleLabel = computed(() => {
    const labels = props.tenant.role_labels;
    if (labels?.length) return labels.join(', ');
    const roles = props.tenant.roles;
    if (!roles?.length) return null;
    return roles.join(', ');
});

const subtitle = computed(() => {
    const parts = [];
    if (props.tenant.title) parts.push(props.tenant.title);
    if (roleLabel.value) parts.push(roleLabel.value);
    return parts.join(' \u2022 ');
});
</script>
